import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import * as api from '../api/client';
import type { FeatureRequestItem } from '../api/client';

const { Text } = Typography;

interface Props {
  onOpenDetails: (id: string) => void;
}

const EDITABLE_STATUSES = ['DRAFT', 'PENDING_REVIEW', 'NEEDS_CLARIFICATION'];

const MyFeatureRequests: React.FC<Props> = ({ onOpenDetails }) => {
  const [items, setItems] = useState<FeatureRequestItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getMyFeatureRequests();
      setItems(res.data.items || []);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load your requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const columns: ColumnsType<FeatureRequestItem> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (_value: string, item: FeatureRequestItem) => (
        <Button type="link" onClick={() => onOpenDetails(item.id)} style={{ padding: 0 }}>
          {item.title}
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 190,
      render: (status: string) => (
        <Tag color={status === 'RELEASED' ? 'green' : status === 'DECLINED' ? 'red' : 'blue'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Votes',
      dataIndex: 'voteCount',
      key: 'voteCount',
      width: 90,
      align: 'center',
    },
    {
      title: 'Comments',
      dataIndex: 'commentCount',
      key: 'commentCount',
      width: 110,
      align: 'center',
    },
    {
      title: 'Last Updated',
      key: 'updatedOn',
      width: 190,
      render: (_value: string, item: FeatureRequestItem) =>
        new Date(item.updatedOn || item.createdOn).toLocaleString(),
    },
    {
      title: 'Admin Note',
      key: 'adminNote',
      ellipsis: true,
      render: (_value: string, item: FeatureRequestItem) => item.rejectionReason || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 170,
      render: (_value: string, item: FeatureRequestItem) => (
        <Space size={6} wrap>
          <Button size="small" onClick={() => onOpenDetails(item.id)}>
            View
          </Button>
          {EDITABLE_STATUSES.includes(item.status) && (
            <Button size="small" type="primary" ghost onClick={() => onOpenDetails(item.id)}>
              Edit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      className="kb-card praxys-feature-card"
      title={<Text strong style={{ color: '#e6edf3' }}>My Feature Requests</Text>}
      bodyStyle={{ padding: 0 }}
    >
      <Table<FeatureRequestItem>
        className="praxys-dark-table"
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={{ pageSize: 8, hideOnSinglePage: true }}
        scroll={{ x: 1120 }}
        locale={{ emptyText: 'No submitted requests yet.' }}
      />
    </Card>
  );
};

export default MyFeatureRequests;
